import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiBookOpen, FiCalendar, FiRefreshCcw } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import styles from './Courses.module.css';

function Courses() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('active'); // Default to 'active'
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [authError, setAuthError] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const fetchCourses = useCallback(async (isRefresh = false) => {
    try {
      if (!isRefresh) {
        setLoading(true);
      }
      
      logger.debug('Fetching courses...');
      const response = await apiService.getCourses();
      logger.debug('Courses response', { success: response.success, coursesCount: response.courses?.length || 0 });
      
      if (response.success && response.courses) {
        logger.debug('Loaded courses', { count: response.courses.length });
        setCourses(response.courses);
        setRetryCount(0);
        setAuthError(false);
        setFetchError(false);
      } else {
        logger.warn('No courses found in response', { response });
        setCourses([]);
        setFetchError(false);
      }
    } catch (error) {
      logger.error('Error fetching courses', { error });
      
      if (error.status === 401 || error.status === 403) {
        logger.debug('Authentication error detected, forcing logout - no retries');
        setCourses([]);
        setRetryCount(0);
        setAuthError(true);
        return;
      } else {
        setFetchError(true);
        if (retryCount < 2 && !isRefresh) {
          logger.debug('Retrying course fetch', { attempt: retryCount + 1, maxAttempts: 2 });
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            fetchCourses(false);
          }, 1000 * (retryCount + 1));
        } else {
          logger.error('Max retries reached, giving up');
          setCourses([]);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchCourses(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, fetchCourses, navigate, location.pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (courses.length > 0) {
        logger.debug('Periodic refresh of courses...');
        fetchCourses(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchCourses, courses.length]);

  const handleCourseClick = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const toggleDescription = (e, courseId) => {
    e.stopPropagation();
    setExpandedDescriptions(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };

  const handleManualRefresh = () => {
    logger.debug('Manual refresh triggered');
    setRetryCount(0);
    setAuthError(false);
    setFetchError(false);
    fetchCourses(false);
  };

  const formatCourseDate = (course) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    if (!course.start_date) {
      return 'TBD';
    }
    
    // Parse date string manually to avoid timezone conversion issues
    // DATE fields come as 'YYYY-MM-DD' format - parse directly to avoid timezone shifts
    const parseDateOnly = (dateString) => {
      if (!dateString) return null;
      
      // If it's already a Date object, extract date parts using UTC methods
      if (dateString instanceof Date) {
        return {
          year: dateString.getUTCFullYear(),
          month: dateString.getUTCMonth(),
          day: dateString.getUTCDate()
        };
      }
      
      // Parse 'YYYY-MM-DD' string directly
      const dateStr = typeof dateString === 'string' ? dateString.split('T')[0] : String(dateString);
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return {
          year: parseInt(parts[0], 10),
          month: parseInt(parts[1], 10) - 1, // Month is 0-indexed
          day: parseInt(parts[2], 10)
        };
      }
      
      // Fallback: parse as Date and use UTC methods
      const d = new Date(dateString);
      return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth(),
        day: d.getUTCDate()
      };
    };
    
    const startDateParts = parseDateOnly(course.start_date);
    const courseStartDate = new Date(startDateParts.year, startDateParts.month, startDateParts.day);
    
    // Format date using the parsed parts directly
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formattedStartDate = `${monthNames[startDateParts.month]} ${startDateParts.day}${startDateParts.year !== now.getFullYear() ? `, ${startDateParts.year}` : ''}`;
    
    // Check if course has ended
    let hasEnded = false;
    let formattedEndDate = '';
    
    if (course.end_date) {
      const endDateParts = parseDateOnly(course.end_date);
      const courseEndDate = new Date(endDateParts.year, endDateParts.month, endDateParts.day);
      hasEnded = courseEndDate < today;
      formattedEndDate = `${monthNames[endDateParts.month]} ${endDateParts.day}${endDateParts.year !== now.getFullYear() ? `, ${endDateParts.year}` : ''}`;
    }
    
    // Determine course status
    const hasStarted = courseStartDate <= today;
    
    if (hasEnded) {
      // Course has ended
      return `Started: ${formattedStartDate} - Ended: ${formattedEndDate}`;
    } else if (hasStarted) {
      // Course has started but not ended
      return `Started: ${formattedStartDate} - Ends: ${formattedEndDate}`;
    } else {
      // Course has not started yet
      return `Starts: ${formattedStartDate} - Ends: ${formattedEndDate}`;
    }
  };

  const filteredCourses = courses.filter(course => {
    const instructorName = `${course.instructor_first_name || ''} ${course.instructor_last_name || ''}`.trim();
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (course.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         instructorName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter =
      activeFilter === 'all' ||
      (activeFilter === 'active' && (course.is_active === 1 || course.is_active === true)) ||
      (activeFilter === 'inactive' && !(course.is_active === 1 || course.is_active === true));
    
    return matchesSearch && matchesFilter;
  });

  const getFilterCounts = () => {
    const counts = { all: courses.length, active: 0, inactive: 0 };
    courses.forEach(course => {
      if (course.is_active === 1 || course.is_active === true) {
        counts.active += 1;
      } else {
        counts.inactive += 1;
      }
    });
    return counts;
  };

  const { all, active, inactive } = getFilterCounts();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>My Courses</h1>
          {fetchError && !loading && (
            <button className={styles.refreshButton} onClick={handleManualRefresh}>
              <FiRefreshCcw size={16} />
              Refresh
            </button>
          )}
        </div>
        <div className={styles.searchBar}>
          <div className={styles.searchIcon}>
            <FiSearch size={16} />
          </div>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.filters}>
        <button
          className={`${styles.filterButton} ${activeFilter === 'all' ? styles.filterButtonActive : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          All Courses
          <span className={styles.filterCount}>{all}</span>
        </button>
        <button
          className={`${styles.filterButton} ${activeFilter === 'active' ? styles.filterButtonActive : ''}`}
          onClick={() => setActiveFilter('active')}
        >
          Active
          <span className={styles.filterCount}>{active}</span>
        </button>
        <button
          className={`${styles.filterButton} ${activeFilter === 'inactive' ? styles.filterButtonActive : ''}`}
          onClick={() => setActiveFilter('inactive')}
        >
          Inactive
          <span className={styles.filterCount}>{inactive}</span>
        </button>
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <FiBookOpen size={32} />
          <p>Loading courses...</p>
        </div>
      ) : filteredCourses.length > 0 ? (
        <div className={styles.coursesGrid}>
          {filteredCourses.map((course) => (
            <div
              key={course.id}
              className={styles.courseCard}
              onClick={() => handleCourseClick(course.id)}
            >
              <div className={styles.courseHeader}>
                <h3 className={styles.courseTitle}>{course.name}</h3>
                <div className={styles.metaRow}>
                  <span className={styles.courseCode}>{course.code}</span>
                  <span className={styles.metaSeparator}>•</span>
                  <span className={styles.courseInstructor}>
                    {course.instructor_first_name} {course.instructor_last_name}
                  </span>
                </div>
                <div className={styles.statusRow}>
                  <div className={styles.statusBadge}>
                    <FiCalendar size={12} />
                    <span>{formatCourseDate(course)}</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.courseContent}>
                <p className={`${styles.courseDescription} ${expandedDescriptions[course.id] ? styles.expanded : ''}`}>
                  {course.description}
                </p>
                {course.description && course.description.length > 150 && (
                  <button 
                    className={styles.readMoreButton}
                    onClick={(e) => toggleDescription(e, course.id)}
                  >
                    {expandedDescriptions[course.id] ? 'Show less' : 'Show more'}
                  </button>
                )}
                
                <div className={styles.courseActions}>
                  <button className={styles.actionButton}>Enter Course</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <FiBookOpen />
          <h3>No courses found</h3>
          {authError ? (
            <p>Your session has expired. You will be redirected to login shortly.</p>
          ) : (
            <p>Try adjusting your search or filters to find what you're looking for.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Courses;
